import type { EventVenueResponse } from "@/api/event/useGetEventVenues";
import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { ActionIcon, Badge, Group, Loader, ScrollArea, Stack, Title, Tooltip } from "@mantine/core";
import { IconFilter, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import type { ExistingEventWithStaging } from "./ExistingEventCard/types";
import { PendingRequestCard } from "./PendingRequestCard/PendingRequestCard";
import { QuestionFilterModal } from "./QuestionFilterModal";
import { StagingGroup } from "./types";

interface PendingRequestsSidebarProps {
  requests: ReservationRequestDto[];
  selectedStagingGroup: StagingGroup | null;
  selectedExistingEvent: ExistingEventWithStaging | null;
  venues: EventVenueResponse[];
  selectedQuestionIds: number[];
  onAddToGroup: (request: ReservationRequestDto) => void;
  onRequestCancelled: (requestId: number) => void;
  onFilterChange: (ids: number[]) => void;
  isRefreshing: boolean;
}

export const PendingRequestsSidebar = ({
  requests,
  selectedStagingGroup,
  selectedExistingEvent,
  venues,
  selectedQuestionIds,
  onAddToGroup,
  onRequestCancelled,
  onFilterChange,
  isRefreshing,
}: PendingRequestsSidebarProps) => {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterModalOpened, setFilterModalOpened] = useState(false);

  const sortedRequests = useMemo(() => {
    return [...requests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [requests, sortOrder]);

  return (
    <Stack h="100%" gap="md">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <Title order={2} c="black">
            대기 중인 요청
          </Title>
          <Badge size="lg">{requests.length}</Badge>
          {isRefreshing && <Loader size="xs" />}
        </Group>
        <Group gap="xs">
          <Tooltip label="질문 필터">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setFilterModalOpened(true)}
            >
              <IconFilter size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={sortOrder === "desc" ? "최신순" : "오래된순"}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
            >
              {sortOrder === "desc" ? (
                <IconSortDescending size={20} />
              ) : (
                <IconSortAscending size={20} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap="md">
          {sortedRequests.map((request) => (
            <PendingRequestCard
              key={request.id}
              request={request}
              selectedStagingGroup={selectedStagingGroup}
              selectedExistingEvent={selectedExistingEvent}
              venues={venues}
              selectedQuestionIds={selectedQuestionIds}
              onAddToGroup={onAddToGroup}
              onRequestCancelled={onRequestCancelled}
            />
          ))}
        </Stack>
      </ScrollArea>

      <QuestionFilterModal
        opened={filterModalOpened}
        onClose={() => setFilterModalOpened(false)}
        selectedQuestionIds={selectedQuestionIds}
        onApply={onFilterChange}
      />
    </Stack>
  );
};
